import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, MapPin, Medal, Share2, Sparkles, Trophy, UserRound } from "lucide-react";
import API_BASE, { withApiCredentials } from "../api";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import { shareAppLink } from "../utils/shareApp";
import { SIDEBAR_SOCIAL_ITEMS } from "./Sidebar";
import "../styles/components/FeedDesktopRightPanel.css";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";

function normalizeId(value) {
  return value === null || value === undefined ? "" : String(value);
}

function buildRelationshipState(user) {
  return {
    following: new Set((user?.following || []).map(normalizeId)),
    friends: new Set((user?.friends || []).map(normalizeId)),
  };
}

function cloneRelationshipState(relationship) {
  return {
    following: new Set(relationship.following),
    friends: new Set(relationship.friends),
  };
}

function notifySocialGraphUpdated() {
  window.dispatchEvent(new Event("friendsUpdated"));
  window.dispatchEvent(new Event("postsUpdated"));
}

function getAvatarSrc(user) {
  const avatar = String(user?.avatar || "").trim();
  if (!avatar) return DEFAULT_AVATAR;
  return /^https?:\/\//i.test(avatar) ? avatar : `${API_BASE}${avatar}`;
}

function humanizeValue(value) {
  return String(value || "")
    .replace(/^[^.]+\./, "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function translateValue(t, value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return t(normalized, { defaultValue: humanizeValue(normalized) });
}

function formatLocation(user) {
  return [user?.city, user?.country]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function parseCreatedAt(value) {
  if (!value) return null;
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 10000000000 ? numeric * 1000 : numeric)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatJoinedDate(value, locale) {
  const date = parseCreatedAt(value);
  if (!date) return "";
  return date.toLocaleString(locale || undefined, {
    month: "short",
    year: "numeric",
  });
}

function getPreferenceChips(user, t) {
  const chipValues = [
    user?.experience,
    user?.fishingStyle,
    user?.waterType,
    user?.rangeType,
    ...(Array.isArray(user?.methods) ? user.methods : []),
    ...(Array.isArray(user?.favoriteSpecies) ? user.favoriteSpecies : []),
  ];

  const seen = new Set();
  return chipValues
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .map((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return "";
      seen.add(key);
      return translateValue(t, value);
    })
    .filter(Boolean);
}

function RankIcon({ rank }) {
  if (rank === 1) {
    return <Trophy size={16} strokeWidth={2.6} aria-hidden="true" />;
  }

  if (rank === 2 || rank === 3) {
    return <Medal size={16} strokeWidth={2.6} aria-hidden="true" />;
  }

  return <span aria-hidden="true">{rank}</span>;
}

export default function FeedDesktopRightPanel({
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
}) {
  const { t, i18n } = useTranslation();
  const [relationship, setRelationship] = useState(() =>
    buildRelationshipState(currentUser)
  );
  const [relationshipLoading, setRelationshipLoading] = useState(false);
  const [pendingFollowById, setPendingFollowById] = useState({});
  const [followError, setFollowError] = useState("");
  const [shareFeedback, setShareFeedback] = useState("");
  const shareFeedbackTimeoutRef = useRef(null);

  const authToken = getActiveAwareAuthToken(
    typeof window !== "undefined" ? window : undefined
  );
  const hasAuthenticatedUser = Boolean(currentUser?.id || currentUser?.username);

  const refreshRelationshipState = useCallback(async () => {
    if (!authToken && !hasAuthenticatedUser) {
      setRelationship(buildRelationshipState(currentUser));
      return;
    }

    setRelationshipLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}/auth/me`,
        withApiCredentials({
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        })
      );

      if (!res.ok) throw new Error("Failed to load relationships");

      const me = await res.json();
      setRelationship(buildRelationshipState(me));
    } catch {
      setRelationship(buildRelationshipState(currentUser));
    } finally {
      setRelationshipLoading(false);
    }
  }, [authToken, currentUser, hasAuthenticatedUser]);

  useEffect(() => {
    refreshRelationshipState();
  }, [refreshRelationshipState]);

  useEffect(() => {
    return () => {
      if (shareFeedbackTimeoutRef.current) {
        window.clearTimeout(shareFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const handleShareAppClick = async () => {
    if (shareFeedbackTimeoutRef.current) {
      window.clearTimeout(shareFeedbackTimeoutRef.current);
      shareFeedbackTimeoutRef.current = null;
    }
    setShareFeedback("");

    try {
      const result = await shareAppLink({
        title: t("shareReelitPro", { defaultValue: "Share ReelitPro" }),
        text: t("shareReelitProText", {
          defaultValue: "Join me on ReelitPro and share your fishing adventures.",
        }),
      });

      if (result === "copied") {
        setShareFeedback(t("shareLinkCopied", { defaultValue: "Link copied" }));
        shareFeedbackTimeoutRef.current = window.setTimeout(() => {
          setShareFeedback("");
          shareFeedbackTimeoutRef.current = null;
        }, 2200);
      }
    } catch {
      setShareFeedback("");
    }
  };

  const leaderboard = useMemo(() => {
    const byUser = new Map();

    (posts || []).forEach((post) => {
      const name = String(post?.user || "").trim();
      if (!name) return;

      const current = byUser.get(name) || {
        name,
        score: 0,
      };

      current.score += 1 + Number(post?.likes?.length || 0);
      byUser.set(name, current);
    });

    return Array.from(byUser.values())
      .sort((left, right) => right.score - left.score)
      .slice(0, 4);
  }, [posts]);

  const suggestedAnglers = useMemo(() => {
    const seen = new Set();
    return (users || [])
      .filter((user) => {
        const username = String(user?.username || "").trim();
        if (!username) return false;
        if (username === currentUser?.username) return false;
        if (seen.has(username)) return false;
        seen.add(username);
        return true;
      })
      .slice(0, 3)
      .map((user) => ({
        id: normalizeId(user.id || user.userId || user.username),
        name: user.username,
        avatar: user.avatar,
        accountType: user.accountType,
        country: user.country,
        city: user.city,
        experience: user.experience,
        fishingStyle: user.fishingStyle,
        waterType: user.waterType,
        rangeType: user.rangeType,
        methods: user.methods,
        favoriteSpecies: user.favoriteSpecies,
        createdAt: user.createdAt,
        followPrivacy: user.followPrivacy,
        role: user.accountType
          ? translateValue(t, user.accountType)
          : t("angler", { defaultValue: "Angler" }),
      }));
  }, [currentUser?.username, t, users]);

  const setFollowPending = (id, value) => {
    setPendingFollowById((current) => {
      const next = { ...current };
      if (value) next[id] = true;
      else delete next[id];
      return next;
    });
  };

  const handleFollowToggle = async (angler, shouldFollow) => {
    const targetId = normalizeId(angler?.id);

    if (!hasAuthenticatedUser || !targetId || pendingFollowById[targetId]) {
      return;
    }

    const snapshot = cloneRelationshipState(relationship);
    setFollowError("");
    setFollowPending(targetId, true);
    setRelationship((current) => {
      const next = cloneRelationshipState(current);
      if (shouldFollow) next.following.add(targetId);
      else next.following.delete(targetId);
      return next;
    });

    try {
      const formData = new FormData();
      if (authToken) {
        formData.append("token", authToken);
      }
      formData.append("target_user_id", targetId);

      const res = await fetch(
        `${API_BASE}${shouldFollow ? "/follow" : "/follow/unfollow"}`,
        withApiCredentials({
          method: "POST",
          body: formData,
        })
      );

      if (!res.ok) throw new Error("Follow action failed");

      notifySocialGraphUpdated();
    } catch {
      setRelationship(snapshot);
      setFollowError(
        t("suggested_anglers_action_error", {
          defaultValue: "That action could not be completed. Please try again.",
        })
      );
      refreshRelationshipState();
    } finally {
      setFollowPending(targetId, false);
    }
  };

  return (
    <aside className="rp-rightpanel" aria-label={t("desktop_right_panel", { defaultValue: "Feed details" })}>
      <section
        className="rp-widget rp-widget--social"
        aria-labelledby="rp-rightpanel-social-title"
      >
        <div className="rp-widget__header rp-widget__header--compact">
          <h3 className="rp-widget__title" id="rp-rightpanel-social-title">
            {t("sidebar_follow_reelitpro")}
          </h3>
        </div>
        <div className="rp-social-rail">
          {SIDEBAR_SOCIAL_ITEMS.map(({ key, labelKey, href, Icon }) => (
            <a
              className={`rp-social-rail__button rp-social-rail__button--${key}`}
              href={href}
              key={key}
              aria-label={t(labelKey)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon aria-hidden="true" />
            </a>
          ))}
        </div>
      </section>

      <button
        type="button"
        className="rp-share-app-rail-button"
        onClick={handleShareAppClick}
        aria-label={t("shareReelitPro", { defaultValue: "Share ReelitPro" })}
      >
        <span className="rp-share-app-rail-button__icon" aria-hidden="true">
          <Share2 size={17} strokeWidth={2.45} />
        </span>
        <span className="rp-share-app-rail-button__copy">
          <strong>{t("shareReelitPro", { defaultValue: "Share ReelitPro" })}</strong>
          <span>{t("shareApp", { defaultValue: "Share app" })}</span>
        </span>
      </button>
      <span className="rp-share-app-rail-status" aria-live="polite">
        {shareFeedback}
      </span>

      {leaderboard.length > 0 ? (
        <section className="rp-widget rp-widget--leaderboard">
          <div className="rp-widget__header">
            <h3 className="rp-widget__title">
              {t("leaderboard", { defaultValue: "Leaderboard" })}
            </h3>
            <span className="rp-widget__more">
              {t("this_week", { defaultValue: "This week" })}
            </span>
          </div>

          {leaderboard.map((entry, index) => (
            <div
              key={entry.name}
              className={`rp-leader-row rp-leader-row--rank-${Math.min(
                index + 1,
                4
              )}`}
            >
              <span
                className="rp-leader-row__rank"
                aria-label={t("fishipedia_rankings_rank_label", {
                  defaultValue: "Rank {{rank}}",
                  rank: index + 1,
                })}
              >
                <RankIcon rank={index + 1} />
              </span>
              <span className="rp-leader-row__name">{entry.name}</span>
              <span className="rp-leader-row__score">{entry.score}</span>
            </div>
          ))}
        </section>
      ) : null}

      {suggestedAnglers.length > 0 ? (
        <section className="rp-widget rp-widget--anglers">
          <div className="rp-widget__header">
            <h3 className="rp-widget__title">
              {t("suggested_anglers", { defaultValue: "Suggested anglers" })}
            </h3>
            <Link className="rp-widget__more" to="/suggested-anglers">
              {t("see_more", { defaultValue: "See more" })}
              <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" />
            </Link>
          </div>

          {followError ? (
            <div className="rp-widget__error">{followError}</div>
          ) : null}

          {suggestedAnglers.map((angler) => {
            const isFollowing = relationship.following.has(angler.id);
            const isPending = Boolean(pendingFollowById[angler.id]);
            const followAllowed =
              angler.followPrivacy !== "nobody" &&
              (angler.followPrivacy !== "friends" ||
                relationship.friends.has(angler.id));
            const location = formatLocation(angler);
            const joinedDate = formatJoinedDate(
              angler.createdAt,
              i18n.resolvedLanguage || i18n.language
            );
            const preferenceChips = getPreferenceChips(angler, t).slice(0, 2);

            return (
              <div key={angler.id} className="rp-angler-row">
                <div className="rp-angler-row__avatar-wrap">
                  <img
                    src={getAvatarSrc(angler)}
                    alt={t("profile_picture", { defaultValue: "Profile picture" })}
                    className="rp-angler-row__avatar"
                  />
                </div>
                <div className="rp-angler-row__info">
                  <button
                    type="button"
                    className="rp-angler-row__name rp-angler-row__name-btn"
                    onClick={() => onOpenUser?.(angler.name)}
                  >
                    {angler.name}
                  </button>
                  <div className="rp-angler-row__role">
                    <UserRound size={13} strokeWidth={2.4} aria-hidden="true" />
                    <span>{angler.role}</span>
                  </div>
                  {location ? (
                    <div className="rp-angler-row__detail">
                      <MapPin size={13} strokeWidth={2.4} aria-hidden="true" />
                      <span>{location}</span>
                    </div>
                  ) : null}
                  {preferenceChips.length > 0 ? (
                    <div className="rp-angler-row__chips">
                      {preferenceChips.map((chip) => (
                        <span key={chip}>
                          <Sparkles size={11} strokeWidth={2.5} aria-hidden="true" />
                          {chip}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {joinedDate ? (
                    <div className="rp-angler-row__joined">
                      {t("joined_date", {
                        defaultValue: "Joined {{date}}",
                        date: joinedDate,
                      })}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={`rp-angler-row__follow${
                    isFollowing ? " rp-angler-row__follow--active" : ""
                  }`}
                  disabled={
                    isPending ||
                    !hasAuthenticatedUser ||
                    relationshipLoading ||
                    !followAllowed
                  }
                  onClick={() => handleFollowToggle(angler, !isFollowing)}
                >
                  {isFollowing
                    ? t("following", { defaultValue: "Following" })
                    : t("follow", { defaultValue: "Follow" })}
                </button>
              </div>
            );
          })}
        </section>
      ) : null}
    </aside>
  );
}
